import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/common';
import { navigate } from '@/providers';
import { AppRoutes, ERROR_MAX_LENGTH, ERROR_REQUIRED_FIELD } from '@/constants';
import {
  showAlertSuccess,
  parseNumber,
  createNameofProxy,
  handleServerValidationErrors,
} from '@/utils';

import { ProfileType } from '@/features/face-id-profiles';
import {
  FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH,
  FACE_ID_PROFILE_FULL_NAME_MIN_LENGTH,
  FACE_ID_PROFILE_REMARKS_MAX_LENGTH,
  PROFILE_TYPE_OPTIONS,
} from '@/features/face-id-profiles/constants';
import { IFaceIdProfileCreation } from '@/features/face-id-profiles/interfaces';
import EmployeeProfileSection from './EmployeeProfileSection';
import GuestProfileSection from './GuestProfileSection';
import { useFaceIdProfileMutation } from '@/features/face-id-profiles/hooks';

const formFields = createNameofProxy<IFaceIdProfileCreation>();

const FaceIdProfileCreation = () => {
  const { creationMutation } = useFaceIdProfileMutation();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = useForm<IFaceIdProfileCreation>();
  const profileTypeValue = watch(formFields.profileType);

  const onSubmit = async (data: IFaceIdProfileCreation) => {
    await creationMutation.mutateAsync(data, {
      onSuccess: () => {
        navigate(AppRoutes.FACE_ID_PROFILES);
        showAlertSuccess(
          `Face profile ID created successfully! You can now register Face ID for ${data.fullName} at the office check-in device.`,
        );
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
  };
  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Header with Title */}
      <h1 className="text-h1 font-bold">Create New Profile</h1>

      <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
        <div className="text-sm flex flex-col gap-y-base-plus p-m">
          <Controller
            name={formFields.fullName}
            control={control}
            rules={{
              required: ERROR_REQUIRED_FIELD,
              minLength: {
                value: FACE_ID_PROFILE_FULL_NAME_MIN_LENGTH,
                message: ERROR_REQUIRED_FIELD,
              },
              maxLength: {
                value: FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH,
                message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH),
              },
            }}
            render={({ field }) => (
              <Input
                onChange={field.onChange}
                label="Full Name"
                placeholder="Full name"
                required
                maxLength={FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH}
                error={errors[formFields.fullName]?.message}
              />
            )}
          />
          <Controller
            name={formFields.profileType}
            control={control}
            rules={{ required: ERROR_REQUIRED_FIELD }}
            render={({ field }) => (
              <Select
                value={field.value?.toString()}
                onValueChange={(value) => field.onChange(parseNumber(value))}
                label="Profile Type"
                required
                error={errors[formFields.profileType]?.message}
              >
                <SelectTrigger allowClear className="w-full">
                  <SelectValue placeholder="Select Profile Type" />
                </SelectTrigger>
                <SelectContent>
                  {PROFILE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {profileTypeValue === ProfileType.Employee && (
            <EmployeeProfileSection formFields={formFields} control={control} errors={errors} />
          )}
          {profileTypeValue === ProfileType.Guest && (
            <GuestProfileSection
              formFields={formFields}
              control={control}
              errors={errors}
              setValue={setValue}
              trigger={trigger}
            />
          )}
          {Number.isInteger(profileTypeValue) && (
            <Controller
              name={formFields.remarks}
              control={control}
              rules={{
                maxLength: {
                  value: FACE_ID_PROFILE_REMARKS_MAX_LENGTH,
                  message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_REMARKS_MAX_LENGTH),
                },
              }}
              render={({ field }) => (
                <Textarea
                  label="Remark"
                  placeholder="Enter remarks"
                  className="[&_textarea]:min-h-28"
                  onChange={field.onChange}
                  maxLength={FACE_ID_PROFILE_REMARKS_MAX_LENGTH}
                  error={errors[formFields.remarks]?.message}
                />
              )}
            />
          )}
        </div>
      </div>
      {/* back and submit button */}
      <div className="flex justify-end gap-s-plus">
        <Button
          className="w-[104px]"
          size="lg"
          variant="cancel"
          borderRadius="soft"
          onClick={() => navigate(AppRoutes.FACE_ID_PROFILES)}
        >
          Back
        </Button>
        <Button
          className="w-[104px]"
          size="lg"
          disabled={creationMutation.isPending}
          variant="primary"
          borderRadius="soft"
          onClick={handleSubmit(onSubmit)}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default FaceIdProfileCreation;
